import { useMutation, useQuery } from "@tanstack/react-query";
import { apiCall } from "../lib/apiClient";
import type { InvitableRole } from "../schemas/userInvite";

/**
 * Veřejné onboarding hooky pro admin-driven pozvánku uživatele (V069).
 *
 *   - useUserInvitationToken(token) — GET peek metadat (consent text), idempotent
 *   - useCompleteUserInvitation()   — POST complete (heslo + consent → User + JWT)
 */

export interface UserInvitationTokenInfo {
  invitation_id: string;
  invited_name: string;
  invited_email: string;
  role: InvitableRole;
  consent_text_version: string | null;
  consent_text_html: string | null;
  expires_at: string;
}

export function useUserInvitationToken(token: string | undefined) {
  return useQuery({
    queryKey: ["public", "user-invitation", token ?? "—"] as const,
    queryFn: () =>
      apiCall<UserInvitationTokenInfo>(
        `/api/public/invitations/${encodeURIComponent(token ?? "")}`,
      ),
    enabled: typeof token === "string" && token.length > 0,
    retry: false,
  });
}

export interface CompleteUserInvitationInput {
  token: string;
  password: string;
  consent_text_version: string;
  consent_types: string[];
}

export function useCompleteUserInvitation() {
  return useMutation({
    mutationFn: (input: CompleteUserInvitationInput) =>
      apiCall<{ user_id: string; tenant_id: string; role: string }>(
        `/api/public/invitations/${encodeURIComponent(input.token)}/complete`,
        {
          method: "POST",
          body: JSON.stringify({
            password: input.password,
            consent_text_version: input.consent_text_version,
            consent_types: input.consent_types,
          }),
        },
      ),
  });
}
